import { useForm as createUseForm } from "@ant-design-vue/use";
import { Col, Row } from "ant-design-vue";
import lodash from "lodash";
import { defineAsyncComponent, reactive, toRaw } from "vue";
import { addModal, setIsLoading as setIsLoadingApp } from "../../../app.logic";
import { setAppPageHeader } from "../../../components/layout/app-layout/app-layout.logic";
import i18n from "../../../i18n";
import store from "../../../store";
import { createButtonEdit, createButtonSave } from "../../../utils/button";
import { modalStyleIFrame } from "../../../utils/constant";
import { checkAttribute, initUseForm, onClickEdit, onClickView, watchRulesChildren } from "../../../utils/form";
import { createLoading } from "../../../utils/loading";
import { createAppPageHeaderTop, createPage } from "../../../utils/page";
import { required, validateError } from "../../../utils/validate";
import { createConditionAction, createConditionForm } from "./user-signature.condition";
import { createColumns, createField } from "./user-signature.section";

const CIFrame = defineAsyncComponent(() => import("../../../components/iframe/CIFrame"));

export const { isLoading, setIsLoading } = createLoading();

export const actionPrefix = "resource/v1/security/userSignature";
export const routerPrefix = "user-signatures";

export const callSave = ({ form }) => store.dispatch(`${actionPrefix}/meSave`, { data: form });
export const callSaveAll = ({ form }) => store.dispatch(`${actionPrefix}/meSaveAll`, { data: form });
export const callSearch = ({ criteria }) => store.dispatch(`${actionPrefix}/meSearch`, { criteria });
export const callSearchEn = ({ criteria }) => store.dispatch(`${actionPrefix}/meSearchEn`, { criteria });
export const callActive = ({ id }) => store.dispatch(`${actionPrefix}/meActive`, { id });
export const callArchive = ({ id }) => store.dispatch(`${actionPrefix}/meDelete`, { id });
export const callDelete = ({ id }) => store.dispatch(`${actionPrefix}/meDelete`, { id, config: { params: { isHardDelete: true } } });
export const callActiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/meActiveAll`, { ids });
export const callArchiveAll = ({ ids }) => store.dispatch(`${actionPrefix}/meDeleteAll`, { ids });
export const callDeleteAll = ({ ids }) => store.dispatch(`${actionPrefix}/meDeleteAll`, { ids, config: { params: { isHardDelete: true } } });

export const callSearchUserCa = ({ criteria }) => store.dispatch(`resource/v1/transaction/userCa/meSearch`, { criteria });
export const callSaveAllUserCa = ({ form }) => store.dispatch(`resource/v1/transaction/userCa/meSaveAll`, { data: form });

export const onSearch = ({ page }) => {
    const where = page.where || [];
    const sort = page.sort || [];
    setIsLoading(true);
    window.onSearch?.({ page });
    return callSearch({ criteria: { ...page, data: [], where, sort } })
        .then(page => (state.page = { ...page, where, sort }))
        .finally(() => setIsLoading(false));
};

export const onClickSaveAll = ({ props, data, formParams, refs }) => {
    return data.useForm
        .validate()
        .then(() => {
            setIsLoadingApp(true);
            const form = { ...data.useForm.modelRef, ...formParams };
            let promises = [];
            if (form.attachments?.[0]?.id && form.attachments?.[0]?.isDelete) {
                promises.push(callDelete({ id: form.attachments?.[0]?.id }))
            } else {
                promises.push(callSaveAll({ form }))
            }
            if (form?.userCas?.length) {
                promises.push(callSaveAllUserCa({ form: form?.userCas }))
            }
            if (form.attachmentEns?.[0]?.id && form.attachmentEns?.[0]?.isDelete) {
                promises.push(callDelete({ id: form.attachmentEns?.[0]?.id }))
            } else {
                promises.push(callSaveAll({ form: { attachments: form.attachmentEns } }))
            }
            return Promise.all(promises)
                .then(() => loadForm({ props, data, refs }))
                .catch(() => loadForm({ props, data, refs }))
                .finally(() => setIsLoadingApp(false))
        })
        .catch(error => {
            validateError({ error });
            throw error;
        });
};

export const onClickModalOk = ({ data }) => {
    return data.useForm
        .validate()
        .then(() => ({ ...toRaw(data.useForm.modelRef), isSave: true }))
        .catch(error => {
            validateError({ error });
            throw error;
        });
};

export const createSearchOneCriteria = ({ id, forLog }) => {
    if (!forLog) {
        return {
            select: ["**"],
            page: 0,
            size: 1,
            where: []
        };
    } else {
        return {
            select: [],
            page: 0,
            size: 1,
            where: [{ name: "id", type: "UUID", operator: "=", value: id }],
            children: []
        };
    }
};

export const createForm = ({ attachments, userCas, attachmentEns }) => {
    const form = {
        attachments: [],
        userCas: [],
        attachmentEns: []
    };
    if (attachments) { form.attachments = attachments; }
    if (userCas) { form.userCas = userCas; }
    if (attachmentEns) { form.attachmentEns = attachmentEns; }
    return form;
};

export const createFormAttachment = () => {
    const userId = lodash.cloneDeep(store.state.resource.auth.info?.user?.id);
    return {
        id: null,
        model: "User",
        group: "Signature",
        refId: userId,
    };
};

export const createRules = () => {
    return {};
};

export const createFormTitle = () => {
    return i18n.global.t("views.attachment.signature");
};

export const loadForm = ({ props, data, refs }) => {
    setIsLoading(true);
    const { useForm } = data;
    const { modelRef } = useForm;
    let promises = [
        callSearch({ criteria: createSearchOneCriteria({}) }),
        callSearchUserCa({ criteria: {
                select: ["**"],
                page: 0,
                size: 1000000,
                sort: [{ name: "updateDate", type: "DESC" }],
                children: [
                    { name: "attachmentCers", select: ["*"], page: 0, size: 1, sort: [{ name: "updateDate", type: "DESC" }] },
                    { name: "attachmentP12s", select: ["*"], page: 0, size: 1, sort: [{ name: "updateDate", type: "DESC" }] }
                ]
            }
        }),
        callSearchEn({ criteria: createSearchOneCriteria({}) }),
    ];
    return Promise.all(promises)
        .then(arr => {
            const page = arr[0];
            let attachments = lodash.cloneDeep(arr?.[0]?.data || []);
            let userCas = lodash.cloneDeep(arr?.[1]?.data || []);
            let attachmentEns = lodash.cloneDeep(arr?.[2]?.data || []);
            lodash.set(modelRef, "attachments", attachments);
            lodash.set(modelRef, "userCas", userCas);
            lodash.set(modelRef, "attachmentEns", attachmentEns);
            page.data[0] = createForm({ attachments, userCas, attachmentEns });
            return page;
        })
        .then(page => initUseForm({ props, data, refs, page }))
        .then(() => loadDefaultForm({ props, data, refs }))
        .then(() => initUseForm({ props, data, refs, page: { data: [modelRef] } }))
        .finally(() => setIsLoading(false));
};

export const loadDefaultForm = () => {
    const promises = [];
    return Promise.all(promises);
};

export const createRulesUserCa = ({ data, key }) => {
    const modelRef = data.useForm?.modelRef || data.form;
    const model = key ? lodash.get(modelRef, key) : modelRef;
    const prefix = key ? `${key}.` : "";
    const rules = {};
    model?.userCas?.forEach((item, index) => {
        if (item.isDelete !== true) {
            rules[`${prefix}userCas.${index}.refProvider`] = [required];
            if (item.attachmentCers?.length === 0) {
                item.attachmentCers.push({});
            }
            if (item.attachmentCers?.length > 0) {
                rules[`${prefix}userCas.${index}.attachmentCers.0.size`] = [required];
            }
            rules[`${prefix}userCas.${index}.attachmentP12s.0.size`] = [required];
            if (!item.id) {
                if (!item.attribute) { item.attribute = {} }
                rules[`${prefix}userCas.${index}.attachmentP12s.0.attribute.password`] = [required];
            }
        }
    });
    return rules;
};

export const initForm = ({ props, data, refs }) => {
    checkAttribute({ props, data, refs });
    data.form = createForm({ props, data, refs });
    data.rules = createRules({ props, data, refs });
    data.useForm = createUseForm(data.form, data.rules);
    data.fields = createField({ props, data, refs, isLoading });
    data.conditionForms = createConditionForm({ props, data, refs, isLoading });
    watchRulesChildren({
        props,
        data,
        refs,
        trigger: () => data.useForm.modelRef.userCas.filter(d => d.isDelete !== true).length,
        createRules: () => createRulesUserCa({ props, data, refs }),
        itemsKey: "userCas"
    });
    loadForm({ props, data, refs }).then(() => {
        if (props.setAppPageHeader) {
            setAppPageHeader({
                slots: {
                    top: () => createAppPageHeaderTop({ props, data, refs, state, routerPrefix, onClickView, onClickEdit }),
                    default: () => createAppPageHeaderDefault({ props, data, refs })
                }
            });
        }
    });
};

export const createAppPageHeaderDefault = ({ props, data, refs }) => {
    const col1 = [];
    const col2 = [];
    const { viewOnly } = props;
    const { useForm } = data;
    const { modelRef, initialModel } = useForm;
    const { id, isActive } = modelRef;
    const canEdit = state.conditionAction.canEdit({ props, record: initialModel });
    if (viewOnly && isActive && canEdit) {
        col1.push(createButtonEdit({ onClick: () => onClickEdit({ record: { id }, routerPrefix }), classStyle: "m-1" }));
    }
    if (!viewOnly && (!id || (id && isActive && canEdit))) {
        col1.push(createButtonSave({ onClick: () => onClickSaveAll({ props, data, refs }), classStyle: "m-1" }));
    }
    return (
        <Row type="flex">
            <Col>{col1}</Col>
            <Col class="ml-auto">{col2}</Col>
        </Row>
    );
};

export const openModalList = ({ bind, onIframeMounted }) => {
    const options = addModal({
        bind: lodash.merge(lodash.cloneDeep(modalStyleIFrame), bind),
        render: () => (
            <CIFrame
                src={`${process.env.VUE_APP_PUBLIC_PATH}/app/user-signatures?isLayout=false&canSelect=true&cacheQuery=true`}
                onIframeMounted={({ refs }) => onIframeMounted({ options, refs })}
            ></CIFrame>
        )
    });
    return options;
};

export const state = reactive({
    columns: createColumns(),
    page: createPage({ options: { select: ["*"] } }),
    tableOptions: { search: { activeKey: "Search" } },
    conditionAction: createConditionAction()
});
